import styled, { css } from 'styled-components';
import { ACCENT_PRIMARY, SURFACE_1, SURFACE_2, SURFACE_3, SURFACE_4, SURFACE_5, SURFACE_6, TEXT_100, TEXT_200, TEXT_300 } from 'style/constants';
import { TEXT_BODY_S_REGULAR, TEXT_LABEL_L_BOLD, TEXT_LABEL_L_MEDIUM, TEXT_LABEL_M_BOLD, TEXT_LABEL_M_MEDIUM, TEXT_LABEL_S_MEDIUM, TEXT_LABEL_XS_MEDIUM, TEXT_TITLE_S } from 'style/design-system/textStyles';
import { SINGLE_LINE_ELLIPSIS } from 'style/mixins';
import { rgba } from 'colors';
import FormInput from 'components/ui/v2/Inputs';
import { IBlockRegion } from '../utils';
import Button from 'components/ui/Button';
import Icon from 'components/ui/Icon';
import Expandable from 'components/ui/Expandable';

export const Wrapper = styled.div<{ region: IBlockRegion; }>`
  margin: 0px 40px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  ${({ region }) => region === 'panel' && css`
    margin: 0;
    gap: 10px;
    position: relative;
  `};
`;

const TABLE_BODY_ROW_CSS = css<{ isRanked: boolean; region: IBlockRegion; }>`
  ${({ region }) => region === 'panel' ? TEXT_LABEL_S_MEDIUM : TEXT_TITLE_S}
  display: flex;
  width: 100%;
  background-color: ${SURFACE_2};
  min-height: ${({ region }) => region === 'panel' ? '48px' : '60px'};
  display: flex;
  align-items: center;

  ${({ isRanked, region }) => isRanked && css`
    & td:nth-child(1) {
      min-width: 76px;
      text-align: center;
      color: ${TEXT_300};
    }

    & td:nth-child(2) {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 166px;
      color: ${TEXT_200};
      padding-left: 20px;
      ${TEXT_LABEL_M_MEDIUM}

      & img {
        width: 40px;
      }
    }

    & td:nth-child(3) {
      width: 100%;
      color: ${TEXT_100};
      padding-left: 20px;
    }

    ${region === 'panel' && css`
      & td:nth-child(1) {
        min-width: 24px;
        text-align: end;
        margin-right: 2px;
      }

      & td:nth-child(2) {
        min-width: 36px;
        justify-content: center;
        padding-left: 0;
        ${TEXT_LABEL_S_MEDIUM}

        & img {
          width: 24px;
        }
      }

      & td:nth-child(3) {
        padding-left: 8px;
      }
    `};
  `};

  ${({ isRanked, region }) => !isRanked && css`
    & td {
      color: ${TEXT_100};
      &:nth-child(2) {
        padding-left: 20px;
        width: 100%;
      }

      &:not(:nth-child(2)) {
        min-width: 76px;
        max-width: 76px;
        display: flex;
        justify-content: center;
      }

      ${region === 'panel' && css`
        &:nth-child(2) {
          padding-left: 8px;
        }

        &:not(:nth-child(2)) {
          min-width: 36px;
        }

        &:nth-child(1) {
          min-width: 24px;
          justify-content: end;
          margin-right: 2px;
        }
      `};
    }
  `};
`;

export const Table = styled.table<{ isRanked: boolean; region: IBlockRegion; }>`
  thead {
    display: flex;
    width: 100%;
    height: 48px;
    background-color: ${SURFACE_3};
    border: 1px solid ${SURFACE_1};
    border-radius: 10px 10px 0 0;

    & tr {
      display: flex;
      width: 100%;

      & th {
        ${({ region }) => region === 'landingPageBlock' ? TEXT_LABEL_M_MEDIUM : TEXT_LABEL_S_MEDIUM}
        color: ${TEXT_300};
        display: flex;
        align-items: center;

        ${({ isRanked, region }) => isRanked && css`
          padding-left: 20px;

          &:nth-child(1) {
            min-width: 76px;
          }

          &:nth-child(2) {
            min-width: 166px;
          }

          &:nth-child(3) {
            width: 100%;
          }

          ${region === 'panel' && css`
            padding-left: 0;

            &:nth-child(1) {
              min-width: 24px;
              justify-content: end;
              margin-right: 2px;
            }

            &:nth-child(2) {
              min-width: 36px;
              justify-content: center;
            }

            &:nth-child(3) {
              padding-left: 8px;
            }
          `};
        `};

        ${({ isRanked, region }) => !isRanked && css`
          &:nth-child(2) {
            padding-left: 20px;
            width: 100%;
          }

          &:not(:nth-child(2)) {
            min-width: 76px;
            display: flex;
            justify-content: center;
          }

          ${region === 'panel' && css`
            &:nth-child(2) {
              padding-left: 8px;
            }

            &:not(:nth-child(2)) {
              min-width: 36px;
            }

            &:nth-child(1) {
              min-width: 24px;
              justify-content: end;
              margin-right: 2px;
            }
          `};
        `};
      }
    }
  }

  tbody {
    display: flex;
    flex-direction: column;
    width: 100%;
    ${({ region }) => region === 'landingPageBlock' && css`max-height: 600px;`};
    overflow-y: auto;

    tr {
      ${TABLE_BODY_ROW_CSS}
      &:last-child {
        border-radius: 0 0 10px 10px;
      }

      &:not(:last-child) {
        border-bottom: 1px solid ${SURFACE_4};
      }

      &:nth-child(1) {
        background-color: ${props => rgba(ACCENT_PRIMARY(props), 0.3)};
        & td:nth-child(1) {
          color: ${ACCENT_PRIMARY};
        }
      }

      &:nth-child(2) {
        background-color: ${props => rgba(ACCENT_PRIMARY(props), 0.2)};
        & td:nth-child(1) {
          color: ${ACCENT_PRIMARY};
        }
      }

      &:nth-child(3) {
        background-color: ${props => rgba(ACCENT_PRIMARY(props), 0.15)};
        & td:nth-child(1) {
          color: ${ACCENT_PRIMARY};
        }
      }
    }
  }
`;

export const CurrentUserPosition = styled.tr`
  ${TABLE_BODY_ROW_CSS}
  border-radius: 8px;
  background-color: ${ACCENT_PRIMARY};

  & td {
    &:nth-child(1) {
      color: ${TEXT_100};
    }
  }
`;

export const LastUpdatedText = styled.span<{ region: IBlockRegion; }>`
  ${({ region }) => region === 'panel' ? TEXT_LABEL_XS_MEDIUM : TEXT_LABEL_M_MEDIUM}
  color: ${TEXT_300};
  text-align: right;
`;

export const OptionsToggle = styled.div`
  display: flex;
  border-radius: 6px;
  height: 44.48px;
  background-color: ${SURFACE_3};
  contain: content;
`;

export const OptionToggle = styled.button<{ active: boolean; }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px 20px 10px 20px;
  ${TEXT_LABEL_M_MEDIUM}
  color: ${TEXT_300};
  ${SINGLE_LINE_ELLIPSIS}

  ${({ active }) => active && css`
    color: ${TEXT_100};
    background-color: ${SURFACE_6};
  `};

  &:hover {
    color: ${TEXT_100};
    background-color: ${SURFACE_6};
  }
`;

export const FieldSet = styled(FormInput.FieldSet)`
  background-color: ${SURFACE_3};
`;

export const FiltersWrapper = styled.div<{ gap: number; }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ gap }) => `${gap}px;`};
`;

export const Filter = styled.div<{ flex?: number; }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ flex }) => flex && css`
    flex: ${flex};
  `};
`;

export const FiltersVisualization = styled.div`
  display: flex;
  flex-direction: column;
`;

export const GameType = styled.span`
  ${TEXT_TITLE_S}
  color: ${TEXT_100};
`;

export const RemainingFilters = styled.span`
  ${TEXT_BODY_S_REGULAR}
  color: ${TEXT_200};
`;

export const FilterIcon = styled(Icon).attrs({
  name: 'textAlignmentCenter',
})`
  margin-right: 14px;
  & svg {
    width: 20px;
    height: 20px;
    & path {
      fill: ${ACCENT_PRIMARY};
    }
  }
`;

export const ArrowIcon = styled(Icon)`
  margin-left: 8px;
  & svg {
    width: 10px;
    height: 10px;
    & path {
      fill: ${ACCENT_PRIMARY};
    }
  }
`;

export const StyledButton = styled(Button)`
  background-color: ${SURFACE_3};
  ${TEXT_BODY_S_REGULAR}
  color: ${TEXT_300};

  &:hover {
    background-color: ${SURFACE_4};
    & svg path {
      fill: ${ACCENT_PRIMARY};
    }
  }
`;

export const StyledExpandable = styled(Expandable)`
  width: 100%;
  & > div {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

export const LeaderboardTypes = styled.div<{ region: IBlockRegion; }>`
  height: 48px;
  display: flex;
  border-radius: 6px;
  contain: content;
  ${TEXT_LABEL_L_MEDIUM}

  ${({ region }) => region === 'panel' && css`
    height: 40px;
    ${TEXT_LABEL_M_BOLD}

    & button {
      ${TEXT_LABEL_M_BOLD}
      &:hover {
        ${TEXT_LABEL_M_BOLD}
      }
    }
  `};
`;

const ACTIVE_TYPE_CSS = css`
  ${TEXT_LABEL_L_BOLD}
  color: ${TEXT_100};
  background-color: ${SURFACE_2};
  border-color: ${ACCENT_PRIMARY};
  border-radius: 6px 6px 0 0;
`;

export const LeaderboardType = styled.button<{ active: boolean; }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 10px 20px 10px 20px;
  color: ${TEXT_300};
  cursor: pointer;
  border-bottom: 1px solid ${SURFACE_5};

  ${({ active }) => active && ACTIVE_TYPE_CSS};

  &:hover {
    ${ACTIVE_TYPE_CSS}
  }
`;
